import { Injectable } from '@angular/core';
import Swal from 'sweetalert2'
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ReplaySubject } from 'rxjs';
import { isDemoOutboundError, showDemoOutboundBlockedDialog } from '../../app/utils/demo-outbound';

@Injectable({
    providedIn: 'root'
})
export class ErrorLogs {
    private historialError: ReplaySubject<any> = new ReplaySubject();
    constructor() {
        window.addEventListener('error', this.showError.bind(this));
        window.addEventListener('responseError', this.showErrorResponse.bind(this));
    }

    showError(event: { detail?: HttpErrorResponse }) {
        const detail = event?.detail;
        if (!(detail instanceof HttpErrorResponse)) {
            return;
        }

        // Missing session fields (Reports analytics auth) — page handlers show the
        // "Session expired" popup + redirect. Skip the raw validation JSON here.
        if (this.isMissingSessionFields(detail.error)) {
            this.historialError.next(detail.error);
            return;
        }

        // Demo outbound (Save & Post / board activate) — friendly Billing CTA, not red Error!
        if (detail.status === 403 && isDemoOutboundError(detail.error)) {
            void showDemoOutboundBlockedDialog();
            this.historialError.next(detail.error);
            return;
        }

        const messageError = this.searchError(detail);
        Swal.fire(
            'Error!',
            Array.isArray(messageError)
                ? messageError.map((error) => `${error} <br>`).join(' ')
                : messageError,
            'error',
        );
        this.historialError.next(detail.error);
    }

    showErrorResponse({ detail }: { detail: HttpResponse<any> }) {
        if (isDemoOutboundError(detail?.body)) {
            void showDemoOutboundBlockedDialog();
            this.historialError.next(detail.body);
            return;
        }
        Swal.fire('Error!', detail.body.message, 'error')
        this.historialError.next(detail.body)
    }

    private isMissingSessionFields(errorBody: unknown): boolean {
        if (!errorBody || typeof errorBody !== 'object') {
            return false;
        }
        const detail = (errorBody as { detail?: unknown }).detail;
        if (!Array.isArray(detail)) {
            return false;
        }
        const missingFields = detail
            .filter((item) => item && typeof item === 'object' && item.type === 'value_error.missing')
            .reduce((acc: any[], item) => acc.concat(Array.isArray(item.loc) ? item.loc : []), []);
        return missingFields.includes('userId') || missingFields.includes('companyId');
    }

    searchError({ error }: HttpErrorResponse) {
        if (!error) {
            return 'Unknown error';
        }
        if (typeof error === 'string') {
            return error;
        }
        if (typeof error.message === 'string') {
            return error.message;
        }
        // Backend validation errors arrive as { status: false, message: string[] }.
        if (Array.isArray(error.message)) {
            return error.message.map((item: unknown) => typeof item === 'string' ? item : JSON.stringify(item));
        }
        if (error.message && typeof error.message === 'object' && Array.isArray(error.message.message)) {
            return error.message.message.map((errorString: string) => `${errorString}`);
        }
        if (Array.isArray(error.detail)) {
            return error.detail.map((item) => item?.msg || JSON.stringify(item)).join('; ');
        }
        if (typeof error.detail === 'string') {
            return error.detail;
        }
        return 'Unknown error';
    }

    showHistorial() {
        return this.historialError;
    }
}
